// SettingsRolesPane.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useNodeTypes } from 'context/NodeTypesContext';
import { useIsMobile } from 'hooks/useDeviceDetect';
import {
  createBaseRole,
  deleteBaseRole,
  subscribeToBaseCustomRoles,
  updateBaseRole,
} from 'services/bases/BaseCustomRoleService';
import {
  setBaseMemberTypeAccess,
  subscribeToMemberTypeAccess,
} from 'services/bases/BaseTypeAccessService';
import { defaultCustomRolePermissions } from 'security/rbac';
import {
  defaultTypeAccess,
  isSystemTypeData,
  normalizeTypeAccess,
  normalizeTypeAccessLevel,
} from 'security/typeAccess';

import './SettingsRolesPane.css';

import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalField from 'components/modal/field/ModalField';
import ModalForm from 'components/modal/form/ModalForm';
import ModalHint from 'components/modal/hint/ModalHint';
import ModalLabel from 'components/modal/label/ModalLabel';
import Modal from 'components/modal/Modal';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';

const BUILTIN_ROWS = [
  {
    key: 'owner',
    name: 'Owner',
    description: 'Full control, including billing management and deleting this knowledge base.',
    builtin: true,
  },
  {
    key: 'admin',
    name: 'Admin',
    description: 'Manage members and settings. Cannot delete the base or change other admins.',
    builtin: true,
  },
  {
    key: 'member',
    name: 'Member',
    description: 'Default knowledge base collaborator. Can add knowledge and connect sources.',
    builtin: true,
    editableTypeAccess: true,
  },
];

const TYPE_ACCESS_OPTIONS = Object.freeze([
  { id: 'none', label: 'No access' },
  { id: 'read', label: 'View only' },
  { id: 'write', label: 'View and change data' },
]);

function permissionLabel(action) {
  if (action === 'types.create') return 'Create collections';
  if (action === 'types.fields.create') return 'Add fields to collections';
  return action;
}

function levelForType(typeAccess, typeId) {
  const access = normalizeTypeAccess(typeAccess);
  const id = String(typeId || '').trim();
  if (id && Object.prototype.hasOwnProperty.call(access.types, id)) {
    return access.types[id];
  }
  return access.default;
}

function listBaseTypesForMatrix(getAll, getLabel) {
  const rows = typeof getAll === 'function' ? getAll() : [];
  return rows
    .filter((row) => {
      const typeId = String(row?.key || row?.id || '').trim();
      if (!typeId) return false;
      if (isSystemTypeData(row)) return false;
      if (row?.showInSelector === false) return false;
      if (row?.enabled === false) return false;
      if (row?.internal === true || row?.internalOnly === true) return false;
      return true;
    })
    .map((row) => {
      const typeId = String(row.key || row.id).trim();
      const fromHelper = typeof getLabel === 'function'
        ? String(getLabel(typeId) || '').trim()
        : '';
      const label = fromHelper
        || String(row?.name || row?.title || typeId).trim()
        || typeId;
      return { typeId, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/**
 * SettingsRolesPane
 *
 * Manage custom member-tier roles, schema-grow permissions, and type access.
 */
const SettingsRolesPane = React.memo(({
  theme,
  base,
}) => {
  const isMobile = useIsMobile();
  const { getAll, getLabel } = useNodeTypes();
  const baseKey = String(base?.key || '').trim();

  const [customRoles, setCustomRoles] = useState([]);
  const [memberTypeAccess, setMemberTypeAccess] = useState(defaultTypeAccess());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [editorErrorMessage, setEditorErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('custom'); // custom | member-types
  const [editingRole, setEditingRole] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState(defaultCustomRolePermissions());
  const [typeAccess, setTypeAccess] = useState(defaultTypeAccess());

  const baseTypes = useMemo(
    () => listBaseTypesForMatrix(getAll, getLabel),
    [getAll, getLabel]
  );

  useEffect(() => {
    if (!baseKey) {
      setCustomRoles([]);
      setMemberTypeAccess(defaultTypeAccess());
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let rolesReady = false;
    let memberReady = false;
    const maybeDone = () => {
      if (rolesReady && memberReady) setLoading(false);
    };

    const unsubRoles = subscribeToBaseCustomRoles(
      baseKey,
      (rows) => {
        setCustomRoles(Array.isArray(rows) ? rows : []);
        rolesReady = true;
        maybeDone();
      },
      (error) => {
        setErrorMessage(error?.message || 'Failed to load roles.');
        rolesReady = true;
        maybeDone();
      }
    );

    const unsubMember = subscribeToMemberTypeAccess(
      baseKey,
      (next) => {
        setMemberTypeAccess(next || defaultTypeAccess());
        memberReady = true;
        maybeDone();
      },
      (error) => {
        setErrorMessage(error?.message || 'Failed to load member type access.');
        memberReady = true;
        maybeDone();
      }
    );

    return () => {
      unsubRoles?.();
      unsubMember?.();
    };
  }, [baseKey]);

  const subtitle = useMemo(() => {
    if (loading) return 'Loading roles...';
    const count = BUILTIN_ROWS.length + customRoles.length;
    return `${count} role${count === 1 ? '' : 's'}`;
  }, [customRoles.length, loading]);

  const editorTitle = useMemo(() => {
    if (editorMode === 'member-types') return 'Member type access';
    return editingRole ? 'Edit role' : 'New role';
  }, [editingRole, editorMode]);

  const openCreate = useCallback(() => {
    setEditorMode('custom');
    setEditingRole(null);
    setName('');
    setDescription('');
    setPermissions(defaultCustomRolePermissions());
    setTypeAccess(defaultTypeAccess());
    setEditorOpen(true);
    setEditorErrorMessage('');
    setErrorMessage('');
    setStatusMessage('');
  }, []);

  const openEdit = useCallback((role) => {
    setEditorMode('custom');
    setEditingRole(role);
    setName(String(role?.name || ''));
    setDescription(String(role?.description || ''));
    setPermissions({
      ...defaultCustomRolePermissions(),
      ...(role?.permissions || {}),
    });
    setTypeAccess(normalizeTypeAccess(role?.typeAccess));
    setEditorOpen(true);
    setEditorErrorMessage('');
    setErrorMessage('');
    setStatusMessage('');
  }, []);

  const openMemberTypeAccess = useCallback(() => {
    setEditorMode('member-types');
    setEditingRole(null);
    setTypeAccess(normalizeTypeAccess(memberTypeAccess));
    setEditorOpen(true);
    setEditorErrorMessage('');
    setErrorMessage('');
    setStatusMessage('');
  }, [memberTypeAccess]);

  const closeEditor = useCallback(() => {
    if (saving) return;
    setEditorOpen(false);
    setEditingRole(null);
    setEditorMode('custom');
    setEditorErrorMessage('');
  }, [saving]);

  const handleTogglePermission = useCallback((action) => {
    setPermissions((prev) => ({
      ...prev,
      [action]: !prev[action],
    }));
  }, []);

  const handleTypeAccessDefaultChange = useCallback((event) => {
    const nextDefault = normalizeTypeAccessLevel(event?.target?.value) || 'write';
    setTypeAccess((prev) => ({
      ...normalizeTypeAccess(prev),
      default: nextDefault,
    }));
  }, []);

  const handleTypeAccessLevelChange = useCallback((typeId, nextLevel) => {
    const id = String(typeId || '').trim();
    if (!id) return;
    const level = normalizeTypeAccessLevel(nextLevel) || 'write';
    setTypeAccess((prev) => {
      const normalized = normalizeTypeAccess(prev);
      return {
        ...normalized,
        types: {
          ...normalized.types,
          [id]: level,
        },
      };
    });
  }, []);

  const handleApplyTypeAccessToAll = useCallback((nextLevel) => {
    const level = normalizeTypeAccessLevel(nextLevel) || 'write';
    setTypeAccess((prev) => {
      const normalized = normalizeTypeAccess(prev);
      const types = { ...normalized.types };
      baseTypes.forEach((row) => {
        types[row.typeId] = level;
      });
      return {
        ...normalized,
        types,
      };
    });
  }, [baseTypes]);

  const handleSave = useCallback(async () => {
    if (!baseKey || saving) return;

    setSaving(true);
    setEditorErrorMessage('');
    try {
      if (editorMode === 'member-types') {
        await setBaseMemberTypeAccess(baseKey, typeAccess);
        setStatusMessage('Member type access updated.');
      } else {
        const cleanedName = String(name || '').trim();
        if (!cleanedName) {
          setEditorErrorMessage('Name is required.');
          setSaving(false);
          return;
        }

        if (editingRole?.key) {
          await updateBaseRole(baseKey, editingRole.key, {
            name: cleanedName,
            description,
            permissions,
            typeAccess,
          });
          setStatusMessage('Role updated.');
        } else {
          await createBaseRole(baseKey, {
            name: cleanedName,
            description,
            permissions,
            typeAccess,
          });
          setStatusMessage('Role created.');
        }
      }
      setEditorOpen(false);
      setEditingRole(null);
      setEditorMode('custom');
    } catch (error) {
      setEditorErrorMessage(error?.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }, [
    description,
    editingRole,
    editorMode,
    name,
    permissions,
    saving,
    typeAccess,
    baseKey,
  ]);

  const handleDelete = useCallback(async (role) => {
    if (!baseKey || !role?.key || saving) return;
    const confirmed = window.confirm(
      `Delete role “${role.name || role.key}”? Members still using it must be reassigned first.`
    );
    if (!confirmed) return;

    setSaving(true);
    setErrorMessage('');
    try {
      await deleteBaseRole(baseKey, role.key);
      setStatusMessage('Role deleted.');
      if (editingRole?.key === role.key) {
        setEditorOpen(false);
        setEditingRole(null);
      }
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to delete role.');
    } finally {
      setSaving(false);
    }
  }, [editingRole, saving, baseKey]);

  const renderTypeAccessEditor = () => (
    <ModalField>
      <ModalLabel label="Type access" />
      <ModalHint hint="Write includes view. Owner and Admin always have full access. System types are not listed and stay available to members. Unspecified types use the default below." />

      <div className="settings-roles-type-default">
        <ModalLabel label="Default for types not listed" />
        <SelectInput
          value={normalizeTypeAccess(typeAccess).default}
          disabled={saving}
          options={TYPE_ACCESS_OPTIONS.map((option) => ({
            value: option.id,
            label: option.label,
          }))}
          onChange={handleTypeAccessDefaultChange}
        />
      </div>

      <div className="settings-roles-type-actions">
        <Button
          label="Set all to No access"
          height="36px"
          fontSize="12px"
          borderRadius="6px"
          enabled={!saving && baseTypes.length > 0}
          onClick={() => handleApplyTypeAccessToAll('none')}
        />
        <Button
          label="Set all to View only"
          height="36px"
          fontSize="12px"
          borderRadius="6px"
          enabled={!saving && baseTypes.length > 0}
          onClick={() => handleApplyTypeAccessToAll('read')}
        />
        <Button
          label="Set all to Write"
          height="36px"
          fontSize="12px"
          borderRadius="6px"
          enabled={!saving && baseTypes.length > 0}
          onClick={() => handleApplyTypeAccessToAll('write')}
        />
      </div>

      {baseTypes.length === 0 ? (
        <div
          className="settings-modal-note"
          style={{ color: theme.foregroundMuted }}
        >
          No base types yet. You can still set the default for types created later.
        </div>
      ) : (
        <div className="settings-roles-type-list">
          {baseTypes.map((row) => (
            <div
              key={row.typeId}
              className="settings-roles-type-row"
              style={{ borderColor: theme.border }}
            >
              <div
                className="settings-roles-type-label"
                style={{ color: theme.foreground }}
              >
                {row.label}
              </div>
              <SelectInput
                value={levelForType(typeAccess, row.typeId)}
                disabled={saving}
                options={TYPE_ACCESS_OPTIONS.map((option) => ({
                  value: option.id,
                  label: option.label,
                }))}
                onChange={(event) => handleTypeAccessLevelChange(row.typeId, event.target.value)}
              />
            </div>
          ))}
        </div>
      )}
    </ModalField>
  );

  return (
    <>
      <SettingsModalPaneHeader
        title="Roles"
        subtitle={subtitle}
        rightContent={(
          <Button
            label="Create Role"
            iconKey="group_add"
            enabled={!saving}
            onClick={openCreate}
          />
        )}
      />

      <SettingsModalPaneContents>
        {errorMessage && (
          <div className="settings-modal-error">{errorMessage}</div>
        )}
        {statusMessage && !errorMessage && (
          <div className="settings-modal-status">{statusMessage}</div>
        )}

        <div className="settings-roles-list">
          {BUILTIN_ROWS.map((row) => (
            <div
              key={row.key}
              className="settings-roles-row"
            >
              <div className="settings-roles-row-main">
                <div
                  className="settings-modal-setting-label"
                  style={{ color: theme.foreground }}
                >
                  {row.name}
                </div>
                <div
                  className="settings-modal-setting-description"
                  style={{ color: theme.foregroundMuted }}
                >
                  {row.description}
                </div>
              </div>

              {/* 
              {row.editableTypeAccess && (
                <div className="settings-roles-row-actions">
                  <Button
                    label="Edit type access"
                    height="36px"
                    fontSize="12px"
                    borderRadius="6px"
                    enabled={!saving}
                    onClick={openMemberTypeAccess}
                  />
                </div>
              )}
              */}
            </div>
          ))}

          {customRoles.map((role) => (
            <div
              key={role.key}
              className="settings-roles-row"
              style={{ borderColor: theme.border }}
            >
              <div className="settings-roles-row-main">
                <div
                  className="settings-modal-setting-label"
                  style={{ color: theme.foreground }}
                >
                  {role.name || role.key}
                </div>
                <div
                  className="settings-modal-setting-description"
                  style={{ color: theme.foregroundMuted }}
                >
                  {role.description || 'Custom member role'}
                </div>
              </div>
              <div
                className="settings-roles-row-actions"
                style={{ paddingLeft: isMobile ? '0px' : undefined }}
              >
                <Button
                  label="Edit"
                  height="36px"
                  fontSize="12px"
                  borderRadius="6px"
                  enabled={!saving}
                  onClick={() => openEdit(role)}
                />
                <Button
                  label="Delete"
                  height="36px"
                  fontSize="12px"
                  borderRadius="6px"
                  enabled={!saving}
                  onClick={() => handleDelete(role)}
                />
              </div>
            </div>
          ))}
        </div>
      </SettingsModalPaneContents>

      <Modal
        iconKey="badge"
        title={editorTitle}
        isOpen={editorOpen}
        height="auto"
        width={isMobile ? '100%' : 560}
        onClose={closeEditor}
      >
        <ModalForm>
          {editorMode === 'custom' && (
            <>
              <ModalField>
                <ModalLabel label="Name" />
                <TextInput
                  value={name}
                  placeholder="e.g. Contributor"
                  disabled={saving}
                  onChange={(event) => setName(event.target.value)}
                  onEnterPress={handleSave}
                />
              </ModalField>

              <ModalField>
                <ModalLabel label="Description (optional)" />
                <TextInput
                  value={description}
                  placeholder="What this role is for"
                  disabled={saving}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </ModalField>

              <ModalField>
                <ModalLabel label="Permissions" />
                <ModalHint hint="Custom roles cannot manage members, billing, or delete/change existing collection structure." />
                <div className="settings-roles-permissions">
                  {Object.keys(defaultCustomRolePermissions()).map((action) => (
                    <label
                      key={action}
                      className="settings-roles-permission"
                      style={{ color: theme.foreground }}
                    >
                      <input
                        type="checkbox"
                        checked={permissions[action] === true}
                        disabled={saving}
                        onChange={() => handleTogglePermission(action)}
                      />
                      <span>{permissionLabel(action)}</span>
                    </label>
                  ))}
                </div>
              </ModalField>
            </>
          )}

          {renderTypeAccessEditor()}

          {editorErrorMessage && (
            <ModalField>
              <ModalHint hint={editorErrorMessage} />
            </ModalField>
          )}
        </ModalForm>

        <ModalButtons
          style={{
            justifyContent: 'space-between',
          }}
        >
          <ModalButtonSection align="START">
            <div />
          </ModalButtonSection>
          <ModalButtonSection align="END">
            <Button
              enabled={!saving}
              iconKey="close"
              label="Cancel"
              onClick={closeEditor}
            />
            <Button
              enabled={!saving}
              iconKey="check"
              label={saving ? 'Saving...' : 'Save'}
              onClick={handleSave}
            />
          </ModalButtonSection>
        </ModalButtons>
      </Modal>
    </>
  );
});

SettingsRolesPane.displayName = 'SettingsRolesPane';

export default SettingsRolesPane;
